import React, { useEffect, useState } from "react";

import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	TextField,
} from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

/**
 * Asks for the closing reason. Calls onConfirm(reasonId|null).
 * With no reasons registered and none required, the parent can skip the dialog,
 * but the dialog also handles that case by confirming right away.
 */
const CloseTicketDialog = ({ open, onClose, onConfirm, busy }) => {
	const [loading, setLoading] = useState(false);
	const [reasons, setReasons] = useState([]);
	const [required, setRequired] = useState(false);
	const [reasonId, setReasonId] = useState("");

	useEffect(() => {
		if (!open) return;
		let cancelled = false;
		setReasonId("");
		setLoading(true);
		api
			.get("/close-reasons")
			.then(({ data }) => {
				if (cancelled) return;
				setReasons(data.reasons || []);
				setRequired(!!data.required);
				setLoading(false);
				if (!data.required && !(data.reasons || []).length) {
					onConfirm(null);
				}
			})
			.catch(err => {
				if (cancelled) return;
				setLoading(false);
				toastError(err);
			});
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const hasReasons = reasons.length > 0;

	return (
		<Dialog open={open && (loading || hasReasons || required)} onClose={onClose} maxWidth="xs" fullWidth>
			<DialogTitle>{i18n.t("quality.closeTitle")}</DialogTitle>
			<DialogContent dividers>
				{loading ? (
					<CircularProgress size={24} />
				) : (
					<TextField
						select
						fullWidth
						variant="outlined"
						margin="dense"
						label={required ? `${i18n.t("quality.reason")} *` : i18n.t("quality.reason")}
						helperText={required ? i18n.t("quality.reasonRequired") : i18n.t("quality.reasonOptional")}
						value={reasonId}
						onChange={e => setReasonId(e.target.value)}
					>
						{!required && (
							<MenuItem value="">{i18n.t("quality.reasonNone")}</MenuItem>
						)}
						{reasons.map(r => (
							<MenuItem key={r.id} value={r.id}>
								{r.name}
							</MenuItem>
						))}
					</TextField>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={busy} variant="outlined" color="secondary">
					{i18n.t("quality.cancel")}
				</Button>
				<Button
					onClick={() => onConfirm(reasonId === "" ? null : reasonId)}
					disabled={busy || loading || (required && reasonId === "")}
					variant="contained"
					color="primary"
				>
					{i18n.t("quality.confirm")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default CloseTicketDialog;
